import React, { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import { FEEDBACK_TYPES } from "@/lib/feedback";
import html2canvas from "html2canvas";
import { X, Upload, Image as ImageIcon, Loader2, Send, Trash2, Camera, Link as LinkIcon } from "lucide-react";

const MAX_BYTES = 2_000_000; // ~2 MB raw

export default function FeedbackModal({ open, onClose, onCreated }) {
    const location = useLocation();
    const fileRef = useRef(null);
    const [type, setType] = useState("bug");
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [attachment, setAttachment] = useState(null); // {data_url, filename, sizeKB}
    const [dragActive, setDragActive] = useState(false);
    const [saving, setSaving] = useState(false);
    const [capturing, setCapturing] = useState(false);

    if (!open) return null;

    const close = () => { if (!saving) onClose?.(); };
    const onBackdrop = (e) => { if (e.target === e.currentTarget) close(); };

    const handleFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Alleen afbeeldingen toegestaan.");
            return;
        }
        if (file.size > MAX_BYTES) {
            toast.error("Bestand te groot (max 2 MB).");
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            setAttachment({
                data_url: e.target.result,
                filename: file.name,
                sizeKB: Math.round(file.size / 1024),
            });
        };
        reader.readAsDataURL(file);
    };

    const onDrop = (e) => {
        e.preventDefault(); e.stopPropagation();
        setDragActive(false);
        const f = e.dataTransfer?.files?.[0];
        handleFile(f);
    };
    const onPaste = (e) => {
        const items = e.clipboardData?.items || [];
        for (const it of items) {
            if (it.kind === "file" && it.type.startsWith("image/")) {
                handleFile(it.getAsFile());
                toast.success(i18n.t("fbm.paste_ok"));
                return;
            }
        }
    };

    // Hide the feedback modal momentarily so it doesn't appear in the screenshot.
    const captureScreen = async () => {
        setCapturing(true);
        const modalEl = document.querySelector('[data-testid="feedback-modal"]');
        const fabEl = document.querySelector('[data-testid="feedback-fab"]');
        // Stash original styles
        const prevModal = modalEl?.style.visibility;
        const prevFab = fabEl?.style.visibility;
        try {
            if (modalEl) modalEl.style.visibility = "hidden";
            if (fabEl) fabEl.style.visibility = "hidden";
            // Wait one paint so the modal is visually gone
            await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

            const target = document.body;
            const canvas = await html2canvas(target, {
                backgroundColor: getComputedStyle(document.documentElement).getPropertyValue("--jr-bg") || "#0A0A0C",
                scale: Math.min(window.devicePixelRatio || 1, 1.5),
                useCORS: true,
                logging: false,
                windowWidth: document.documentElement.clientWidth,
                windowHeight: document.documentElement.clientHeight,
                ignoreElements: (el) => el?.dataset?.testid === "feedback-modal" || el?.dataset?.testid === "feedback-fab",
            });

            // Compress: scale down to max 1200px wide and use JPEG (smaller than PNG)
            const maxW = 1200;
            const scale = canvas.width > maxW ? maxW / canvas.width : 1;
            let outCanvas = canvas;
            if (scale < 1) {
                outCanvas = document.createElement("canvas");
                outCanvas.width = Math.round(canvas.width * scale);
                outCanvas.height = Math.round(canvas.height * scale);
                const ctx = outCanvas.getContext("2d");
                ctx.drawImage(canvas, 0, 0, outCanvas.width, outCanvas.height);
            }
            const data_url = outCanvas.toDataURL("image/jpeg", 0.7);
            if (data_url.length > MAX_BYTES) {
                toast.error("Screenshot te groot — probeer paste of upload i.p.v. capture.");
                return;
            }
            setAttachment({
                data_url,
                filename: `screen-${Date.now()}.jpg`,
                sizeKB: Math.round(data_url.length / 1024),
            });
            toast.success(i18n.t("fbm.capture_ok"));
        } catch (err) {
            toast.error(i18n.t("fbm.capture_failed"));
        } finally {
            if (modalEl) modalEl.style.visibility = prevModal || "";
            if (fabEl) fabEl.style.visibility = prevFab || "";
            setCapturing(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (title.trim().length < 2 || message.trim().length < 2) {
            toast.error(i18n.t("fbm.fill_required"));
            return;
        }
        setSaving(true);
        try {
            await api.post("/feedback", {
                type, title: title.trim(), message: message.trim(),
                page_url: window.location.pathname + window.location.search + window.location.hash,
                attachment: attachment ? { data_url: attachment.data_url, filename: attachment.filename } : null,
            });
            toast.success(i18n.t("fbm.sent_ok"));
            setType("bug"); setTitle(""); setMessage(""); setAttachment(null);
            onCreated?.();
            onClose?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || i18n.t("inbox.send_failed"));
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-[60] grid place-items-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={onBackdrop} onPaste={onPaste}>
            <form onSubmit={submit}
                className="w-full max-w-lg bg-[var(--jr-surface)] border border-white/10 rounded-2xl jr-fadein"
                data-testid="feedback-modal" onClick={(e) => e.stopPropagation()}>
                <div className="p-6 border-b border-white/8 flex items-start justify-between">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)]">{i18n.t("fbm.give_feedback")}</div>
                        <h3 className="font-display text-2xl tracking-tight mt-1">{i18n.t("fbm.help_us")}</h3>
                        <p className="text-xs text-[var(--jr-text-2)] mt-1">
                            {i18n.t("fbm.we_read")}
                        </p>
                    </div>
                    <button type="button" onClick={close} aria-label="Sluit"
                        data-testid="feedback-close"
                        className="text-[var(--jr-text-2)] hover:text-white">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                    {/* Type picker */}
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Type</label>
                        <div className="mt-2 grid grid-cols-2 gap-2" role="radiogroup">
                            {Object.entries(FEEDBACK_TYPES).map(([key, t]) => {
                                const Icon = t.icon;
                                const active = type === key;
                                return (
                                    <button key={key} type="button" role="radio" aria-checked={active}
                                        onClick={() => setType(key)} data-testid={`feedback-type-${key}`}
                                        className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                                            active
                                                ? "border-white/30 bg-white/[0.04]"
                                                : "border-white/8 hover:border-white/20 hover:bg-white/[0.02]"
                                        }`}>
                                        <Icon size={16} style={{ color: t.color }} />
                                        <div className="min-w-0">
                                            <div className="text-sm text-white">{t.label}</div>
                                            <div className="text-[10px] text-[var(--jr-text-3)] truncate">{t.desc}</div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Title */}
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Titel</label>
                        <input value={title} onChange={(e) => setTitle(e.target.value)}
                            maxLength={120} required data-testid="feedback-title"
                            placeholder={type === "bug" ? "Kort: wat ging er mis?" : "Korte samenvatting"}
                            className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none" />
                        <div className="text-right text-[10px] text-[var(--jr-text-3)] mt-0.5">{title.length}/120</div>
                    </div>

                    {/* Message */}
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Details</label>
                        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5}
                            maxLength={4000} required data-testid="feedback-message"
                            placeholder={
                                type === "bug"
                                    ? i18n.t("fbm.bug_ph")
                                    : i18n.t("fbm.idea_ph")
                            }
                            className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none resize-none" />
                    </div>

                    {/* Attachment / screenshot */}
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Screenshot (optioneel)</label>
                            {!attachment && (
                                <button type="button" onClick={captureScreen} disabled={capturing}
                                    data-testid="feedback-capture-screen"
                                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-1 rounded-full bg-[var(--jr-info)]/15 text-[var(--jr-info)] border border-[var(--jr-info)]/30 hover:bg-[var(--jr-info)]/25 disabled:opacity-50">
                                    {capturing ? <Loader2 size={10} className="animate-spin" /> : <Camera size={10} />}
                                    {capturing ? "Vastleggen…" : "Capture scherm"}
                                </button>
                            )}
                        </div>
                        {attachment ? (
                            <div className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-3 flex items-center gap-3" data-testid="feedback-attachment-preview">
                                <img src={attachment.data_url} alt="" className="w-16 h-16 rounded object-cover border border-white/10" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-white truncate">{attachment.filename}</div>
                                    <div className="text-[10px] text-[var(--jr-text-3)] font-mono uppercase tracking-[0.14em]">{attachment.sizeKB} KB</div>
                                </div>
                                <button type="button" onClick={() => setAttachment(null)}
                                    aria-label={i18n.t("fbm.remove_attachment")}
                                    data-testid="feedback-attachment-remove"
                                    className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-[var(--jr-danger)]">
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ) : (
                            <div
                                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                                onDragLeave={() => setDragActive(false)}
                                onDrop={onDrop}
                                onClick={() => fileRef.current?.click()}
                                data-testid="feedback-attachment-drop"
                                className={`cursor-pointer rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors ${
                                    dragActive
                                        ? "border-[var(--jr-brand)] bg-[var(--jr-brand)]/5"
                                        : "border-white/10 hover:border-white/25 hover:bg-white/[0.02]"
                                }`}>
                                <div className="flex items-center justify-center gap-2 text-sm text-[var(--jr-text-2)]">
                                    <Upload size={14} />
                                    {i18n.t("fbm.drop_pre")} <span className="text-white underline underline-offset-2">{i18n.t("fbm.drop_pick")}</span> · {i18n.t("fbm.drop_post")} <span className="text-[var(--jr-info)]">&ldquo;{i18n.t("fbm.capture_screen")}&rdquo;</span>
                                </div>
                                <div className="text-[10px] text-[var(--jr-text-3)] mt-1 font-mono uppercase tracking-[0.14em]">PNG/JPG · max 2 MB</div>
                                <input ref={fileRef} type="file" accept="image/*" hidden
                                    data-testid="feedback-attachment-input"
                                    onChange={(e) => handleFile(e.target.files?.[0])} />
                            </div>
                        )}
                    </div>

                    {/* Context badge — URL of the page they're on */}
                    <div className="rounded-lg border border-white/8 bg-[var(--jr-surface-2)] p-2.5 flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.14em]"
                        data-testid="feedback-context-url">
                        <LinkIcon size={11} className="text-[var(--jr-text-3)] shrink-0" />
                        <span className="text-[var(--jr-text-3)] shrink-0">Pagina:</span>
                        <span className="text-[var(--jr-info)] truncate">{location.pathname}{location.search}{location.hash}</span>
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-white/8 flex items-center justify-end gap-2 bg-[var(--jr-surface-2)]/40">
                    <button type="button" onClick={close} disabled={saving}
                        className="px-4 py-2 rounded-full border border-white/10 text-sm text-[var(--jr-text-2)] hover:bg-white/5 disabled:opacity-50">
                        Annuleer
                    </button>
                    <button type="submit" disabled={saving} data-testid="feedback-submit"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-60">
                        {saving ? <><Loader2 className="animate-spin" size={14} /> {i18n.t("fbm.sending")}</> : <><Send size={14} /> {i18n.t("fbm.send_feedback")}</>}
                    </button>
                </div>
            </form>
        </div>
    );
}
